import { Observable, of } from 'rxjs';

import { catchError, tap } from 'rxjs/operators';

import {
	Container,
	Service,
	ProgrammingLanguage,
	LearningExperienceType,
	ModalService,
} from '../../../../symphony';

import {
	checkForInputs,
	getStructuredWebCode,
	wrapByTag,
} from '../../utils/helpers';
import {
	LanguageRoutes,
} from '../../constants/LanguageTypes';
import {
	ActionType,
	IError,
	RequestType,
} from '../../global-interface';
import { SlApiService } from '../sl-api.service';
import { SlActions } from '../ActionsService/sl-actions';
import { FLASH_MESSAGES } from '../../constants/FlashMessages';
import { SlDataService } from '../sl-data.service';
import { ErrorCodes, handleError } from '../../utils/networkErrorHandling';
import { IFlashMessageTypes } from '../../../../shared/public/SlFlashMessage/SlFlashMessage';
import { ProgrammingLanguageToCodeLanguages } from '../../../../shared/public/constants/constants';
import {
	ICodeCompilationResponse,
} from '../../../../api/public/code-api';
import { CodeLanguages } from '../../../../api/private/global.interface';
import { PlaygroundOutputType, MaterialType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { lePlaygroundContext, tiyCodeInputModalId } from '../../global-constants';
import { SlMaterialService } from './sl-material.service';

@Service()
export class SlCodeService {
	private api = Container.take(lePlaygroundContext.value, SlApiService);

	private dataService = Container.take(lePlaygroundContext.value, SlDataService);

	private modalService = Container.take('global', ModalService);

	private materialService = Container.take(lePlaygroundContext.value, SlMaterialService);

	private actions$ = Container.take(lePlaygroundContext.value, SlActions).actions$;

	private isTryItYourself: boolean = Container.take(
		lePlaygroundContext.value,
		'isTryItYourself',
	);

	private materialId: number = Container.take(
		lePlaygroundContext.value,
		'materialId',
	);

	private mutateOutputResponse = (data, isDark?: boolean) => {
		const { isDarkMode$ } = this.dataService;
		let css = '';
		let htmlCode = data?.output;
		const darkMode = isDark || isDarkMode$.value;
		if (
			data?.outputType === PlaygroundOutputType.Web
			&& data?.outputStyle
		) {
			const localDocument = new DOMParser().parseFromString(
				htmlCode,
				'text/html',
			);
			if (localDocument.getElementById('custom-style')) {
				localDocument.getElementById('custom-style').remove();
				htmlCode = localDocument.documentElement.innerHTML;
				htmlCode = wrapByTag(htmlCode, 'html');
			}
			const style = darkMode
				? data?.outputStyle.dark
				: data?.outputStyle.light;
			css = `<style id="custom-style">${style} </style>`;
			const includeHead = htmlCode ? htmlCode.includes('<head>') : false;
			const htmlTagIndex = htmlCode.indexOf('<html>') + 6;
			if (!includeHead) {
				htmlCode = [
					htmlCode.slice(0, htmlTagIndex),
					`<head>${css}</head>`,
					htmlCode.slice(htmlTagIndex),
				].join('');
			} else {
				const closeHeadTagIndex = htmlCode.indexOf('</head>');
				htmlCode = [
					htmlCode.slice(0, closeHeadTagIndex),
					css,
					htmlCode.slice(closeHeadTagIndex),
				].join('');
			}
		}
		return {
			...data,
			output: htmlCode,
		};
	};

	private solveWebCode = () => {
		const { sourceCode$, cssCode$, jsCode$ } = this.dataService;

		const solutions = {
			typeId: MaterialType.TryItYourself,
			materialRelationId: this.materialId,
			codes: [
				{
					source: sourceCode$.value,
					languageId: ProgrammingLanguage.Html,
					inputs: null,
				},
				{
					source: jsCode$.value,
					languageId: ProgrammingLanguage.Js,
					inputs: null,
				},
				{
					source: cssCode$.value,
					languageId: ProgrammingLanguage.Css,
					inputs: null,
				},
			],
		};

		this.actions$.next({
			type: ActionType.SolveMaterial,
			requestType: RequestType.Parallel,
			data: {
				solveMaterialPayload: {
					solutions: [solutions],
					experience_type_id: LearningExperienceType.Course,
				},
			},
		});
	};

	private solveMaterial = (data?: {
		input?: string;
		language?: CodeLanguages | LanguageRoutes | string;
	}) => {
		const { codeData$, sourceCode$ } = this.dataService;
		const programmingLanguages = Object.keys(
			ProgrammingLanguageToCodeLanguages,
		);
		const language = codeData$.value.language;
		const languageId = programmingLanguages.find(
			(item) => ProgrammingLanguageToCodeLanguages[item] === language,
		);
		const solutions = {
			typeId: MaterialType.TryItYourself,
			materialRelationId: this.materialId,
			codes: [
				{
					source: sourceCode$.value,
					languageId: +languageId as ProgrammingLanguage,
					inputs: data.input ? [data.input] : null,
				},
			],
		};

		this.materialService.solveMaterial({
			solutions: [solutions],
			experience_type_id: LearningExperienceType.Course,
		});
	};

	public compileCode = (data?: {
		input?: string;
		language?: CodeLanguages | LanguageRoutes | string;
	}): Observable<ICodeCompilationResponse> => {
		const { codeData$, sourceCode$, inputs$ } = this.dataService;
		if (this.isTryItYourself) {
			this.solveMaterial(data);
			return of(null);
		}
		this.dataService.animationLoader$.next(true);
		return this.api
			.compileCode({
				language: (codeData$.value.language) as CodeLanguages,
				code: sourceCode$.value,
				input: data?.input || inputs$.value,
			})
			.pipe(
				tap((response: ICodeCompilationResponse) => {
					this.dataService.output$.next(
						this.mutateOutputResponse(response.data),
					);
					this.dataService.animationLoader$.next(false);
				}),
				catchError((err: IError[]) => {
					handleError(err, {
						[ErrorCodes.error400]: () => {
							this.dataService.flashMessage$.next({
								type: IFlashMessageTypes.ERROR,
								message: FLASH_MESSAGES.couldNotCompileCode,
							});
						},
					});
					this.dataService.animationLoader$.next(false);
					return of(null);
				}),
			);
	};

	public compileWebCodeHandler = (
		sourceCode: string,
		cssCode: string,
		jsCode: string,
	): void => {
		const result = getStructuredWebCode(sourceCode, cssCode, jsCode);
		this.dataService.output$.next({
			output: result,
			outputType: PlaygroundOutputType.Web,
		});
	};

	private onCompileWebCode = () => {
		const {
			sourceCode$, jsCode$, cssCode$, materialData$,
		} = this.dataService;

		if (this.isTryItYourself) {
			this.solveWebCode();
		}

		this.compileWebCodeHandler(
			sourceCode$.value,
			cssCode$.value,
			jsCode$.value,
		);
	};

	public onCompileCode = (): Observable<ICodeCompilationResponse> => {
		const {
			isWeb$, codeData$, sourceCode$,
		} = this.dataService;

		if (!isWeb$.value) {
			if (checkForInputs(codeData$?.value.language, sourceCode$.value)) {
				this.modalService.open(tiyCodeInputModalId);
			} else {
				return this.compileCode({});
			}
		} else {
			this.onCompileWebCode();
			return of(null);
		}
		return of(null);
	};
}

export interface IContinueAfterCodeSavingHandlerArgs {
	callback: () => void;
	publicId: string;
	isTryItYourself: boolean;
}
